// React
import { useMemo, Fragment } from "react"

// Router
import { useNavigate } from "@/lib/router"
import { Routes } from "@/constants/routes"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Table
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
} from "@/lib/table"

// UI
import { CardBody, CardWrapper } from "@/components/Card"
import { Heading } from "@/components/Typography"
import {
	Table,
	TableBody,
	TableDataCell,
	TableRowCell,
	TableHead,
	TableHeading,
} from "@/components/table-controls/TableItems"
import { Button } from "@/components/Button"

// Translations
import { useLang } from "@/context/lang"
import { useTrans } from "@/lib/i18n"

// Query
import { useProjectOverviewStatsQuery } from "@/api/graphql"

// Constants
import {
	ProjectCrowdfundingTypes,
	endDateStatistics,
} from "@/constants/constants"

// Types
interface ProjectOverviewData {
	label: string
	value: string | number
}

/**
 * ProjectsOverviewTable
 * Displays static overview data for Zonhub Crowdfunded Projects
 */
export function ProjectsOverviewTable() {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Router
	const navigate = useNavigate()

	// Translations
	const t = useTrans("dashboard")
	const { formatCurrency, formatNumber } = useLang()

	// Query
	const { data } = useProjectOverviewStatsQuery({
		project_types: ProjectCrowdfundingTypes,
		date: endDateStatistics,
	})

	// Transform query data to table format
	const tableData = useMemo(() => {
		if (!data?.platform_statistics) return []

		const stats = data.platform_statistics
		return [
			{
				label: t("dashboard.statistics.platform.total_raised"),
				value: formatCurrency(stats.total_amount_invested ?? 0, {
					maximumFractionDigits: 0,
				}),
			},
			{
				label: t(
					"dashboard.statistics.platform.percentage_projects_financed",
				),
				value: `${formatNumber(
					stats.percentage_of_projects_successfully_funded ?? 0,
				)}%`,
			},
			{
				label: t(
					"dashboard.statistics.platform.total_projects_financed",
				),
				value: formatNumber(stats.total_projects ?? 0),
			},
			{
				label: t("dashboard.statistics.platform.total_projects_active"),
				value: formatNumber(stats.total_projects_active ?? 0),
			},
			{
				label: t("dashboard.statistics.platform.total_capital_repaid"),
				value: formatCurrency(stats.capital_repaid ?? 0, {
					maximumFractionDigits: 0,
				}),
			},
			{
				label: t("dashboard.statistics.platform.total_projects_repaid"),
				value: formatNumber(stats.total_projects_repaid ?? 0),
			},
			{
				label: t(
					"dashboard.statistics.platform.total_projects_ended_partial_repaid",
				),
				value: formatNumber(stats.total_projects_ended_partial ?? 0),
			},
			{
				label: t("dashboard.statistics.platform.total_capital_loss"),
				value: formatCurrency(stats.capital_loss ?? 0, {
					maximumFractionDigits: 0,
				}),
			},
		]
	}, [data, formatNumber, formatCurrency, t])

	// Table setup
	const columnHelper = createColumnHelper<ProjectOverviewData>()
	const columns = useMemo(
		() => [
			columnHelper.accessor("label", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard.statistics.platform.metric")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell className="font-medium">
						{info.getValue()}
					</TableDataCell>
				),
			}),
			columnHelper.accessor("value", {
				header: () => (
					<TableHeading variant="static">
						{t("dashboard.statistics.platform.value")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
		],
		[columnHelper, t],
	)

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
	})

	return (
		<CardWrapper className="mb-4">
			<CardBody>
				<Heading as="h3" className="mb-6">
					{t("dashboard.statistics.platform.projects_overview_title")}
				</Heading>
				<p className="mb-4 text-sm text-gray-600">
					{t("dashboard.statistics.platform.as_per_date", {
						date: DateTime.fromJSDate(
							endDateStatistics,
						).toLocaleString(),
					})}
				</p>

				<Table>
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRowCell
								key={headerGroup.id}
								withHover={false}
							>
								{headerGroup.headers.map((header) => (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableHead>
					<TableBody>
						{table.getRowModel().rows.map((row, index) => (
							<TableRowCell
								key={row.id}
								isOdd={index % 2 === 0}
								withHover={false}
							>
								{row.getAllCells().map((cell) => (
									<Fragment key={cell.id}>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableBody>
				</Table>

				<div className="mt-4 flex flex-col items-center">
					<Button onClick={() => navigate(Routes.StatisticsProjects)}>
						{t(
							"dashboard.statistics.platform.view_project_details",
						)}
					</Button>
				</div>
			</CardBody>
		</CardWrapper>
	)
}
